import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { PdsOrdersGateway } from '../gateway';
import {
  DEFAULT_HISTORY_QUERY,
  type HistoryQuery,
  type HistoryRow,
} from '../history';
import { resolveGateway } from '../resolve-gateway';

const SEARCH_DEBOUNCE_MS = 300;

export type HistoryLoadStatus = 'idle' | 'loading' | 'ready' | 'error';
export type HistoryFilters = Partial<
  Omit<HistoryQuery, 'page' | 'pageSize' | 'q'>
>;

export const createPdsHistoryStore = (id: string, gateway: PdsOrdersGateway) =>
  defineStore(id, () => {
    const query = ref<HistoryQuery>({ ...DEFAULT_HISTORY_QUERY });
    const rows = ref<HistoryRow[]>([]);
    const total = ref(0);
    const overallTotal = ref<number | null>(null);
    const status = ref<HistoryLoadStatus>('idle');
    let latestRequest = 0;
    let searchTimer: ReturnType<typeof setTimeout> | undefined;

    const pageCount = computed(() =>
      Math.max(1, Math.ceil(total.value / query.value.pageSize)),
    );
    const firstShown = computed(() =>
      total.value ? (query.value.page - 1) * query.value.pageSize + 1 : 0,
    );
    const lastShown = computed(() =>
      Math.min(query.value.page * query.value.pageSize, total.value),
    );
    // Les filtres qui changent la liste, hors recherche et état qui ont leur propre place à l'écran.
    const activeFilters = computed(
      () =>
        [
          query.value.period !== 'all',
          query.value.payment !== 'all',
          query.value.channel !== 'all',
          query.value.minTotal !== null || query.value.maxTotal !== null,
        ].filter(Boolean).length,
    );

    const load = async (): Promise<void> => {
      const mine = ++latestRequest;
      status.value = 'loading';
      try {
        const page = await gateway.searchHistory({ ...query.value });
        // Une demande plus récente est partie : sa réponse seule compte.
        if (mine !== latestRequest) return;
        rows.value = page.rows;
        total.value = page.total;
        if (query.value.page > pageCount.value) {
          query.value.page = pageCount.value;
          return load();
        }
        status.value = 'ready';
      } catch {
        if (mine === latestRequest) status.value = 'error';
      }
    };

    const setPage = (page: number) => {
      query.value.page = Math.min(Math.max(1, page), pageCount.value);
      return load();
    };

    const setPageSize = (pageSize: number) => {
      query.value.pageSize = pageSize;
      query.value.page = 1;
      return load();
    };

    const setFilters = (filters: HistoryFilters) => {
      Object.assign(query.value, filters, { page: 1 });
      return load();
    };

    // La frappe attend sa fin : une requête par lettre saturerait le serveur et l'écran.
    const setSearch = (text: string) => {
      query.value.q = text;
      query.value.page = 1;
      clearTimeout(searchTimer);
      searchTimer = setTimeout(load, SEARCH_DEBOUNCE_MS);
    };

    const resetFilters = () => {
      clearTimeout(searchTimer);
      query.value = {
        ...DEFAULT_HISTORY_QUERY,
        pageSize: query.value.pageSize,
      };
      return load();
    };

    // Nombre total de commandes, pour le menu : une page d'une ligne, sans toucher à la liste affichée.
    const refreshOverallTotal = async () => {
      try {
        const page = await gateway.searchHistory({
          ...DEFAULT_HISTORY_QUERY,
          pageSize: 1,
        });
        overallTotal.value = page.total;
      } catch {
        overallTotal.value = null;
      }
    };

    const stopTimers = () => clearTimeout(searchTimer);

    return {
      query,
      rows,
      total,
      overallTotal,
      status,
      pageCount,
      firstShown,
      lastShown,
      activeFilters,
      load,
      setPage,
      setPageSize,
      setFilters,
      setSearch,
      resetFilters,
      refreshOverallTotal,
      stopTimers,
    };
  });

export const usePdsHistoryStore = createPdsHistoryStore(
  'pds-history',
  resolveGateway(),
);
